"use client";

import { useState, useEffect, useRef } from "react";
import { Input, Loading } from "react-vant";
import { debounce } from "radash";
import viewcartApi from "@/service/modules/viewcartApi";
import styles from "@/styles/global/suburb-select-dropdown.module.scss";

interface SuburbOption {
  suburbID: string | number;
  description: string;
  suburbName: string;
  name?: string;
}

interface SuburbSelectProps {
  value?: string | number;
  onChange?: (value: string | number, option: SuburbOption) => void;
  placeholder?: string;
  selectedLabel?: string;
  notAutoSelect?: boolean;
  className?: string;
}

export default function SuburbSelectDropdown({
  value,
  onChange,
  placeholder = "Search suburb...",
  selectedLabel,
  notAutoSelect,
  className,
}: SuburbSelectProps) {
  const [options, setOptions] = useState<SuburbOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputValueRef = useRef(inputValue);
  const selectedNameRef = useRef(selectedName);
  const optionsRef = useRef(options);
  const loadingRef = useRef(loading);

  inputValueRef.current = inputValue;
  selectedNameRef.current = selectedName;
  optionsRef.current = options;
  loadingRef.current = loading;

  useEffect(() => {
    if (value && selectedLabel) {
      const selectedOption = {
        suburbID: value,
        suburbName: selectedLabel,
        description: "",
      };
      setOptions([selectedOption]);
      setSelectedName(selectedLabel);
      setInputValue(selectedLabel);
      return;
    }

    if (options.find((opt) => opt.suburbID == value)) return;
    if (value) getSuburbInfo(value);
  }, [value, selectedLabel]);

  useEffect(() => {
    if (notAutoSelect) return;
    if (options.length === 1) {
      const onlyOption = options[0];
      setSelectedName(onlyOption.suburbName);
      setInputValue(onlyOption.suburbName);
      onChange?.(onlyOption.suburbID, { ...onlyOption, name: onlyOption.suburbName });
    }
  }, [options]);

  useEffect(() => {
    const restoreSelectedName = () => {
      const keyword = inputValueRef.current;
      const hasNoSearchResult =
        keyword.length >= 3 && !loadingRef.current && optionsRef.current.length === 0;

      if (selectedNameRef.current && (!keyword || hasNoSearchResult)) {
        setInputValue(selectedNameRef.current);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        restoreSelectedName();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchOptions = async (keyword: string) => {
    if (!keyword) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await viewcartApi.getSuburbList(keyword);
      const data = response.data?.suburbs;
      setOptions(data || []);
    } catch (error) {
      console.error("Error fetching options:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useRef(
    debounce({ delay: 300 }, (keyword: string) => {
      fetchOptions(keyword);
    })
  ).current;

  const getSuburbInfo = async (suburbID: string | number) => {
    try {
      const res = await viewcartApi.getSuburbinfo(suburbID);
      const { suburb } = res.data;
      setOptions([suburb]);
      setSelectedName(suburb.suburbName);
      setInputValue(suburb.suburbName);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (val: string) => {
    setInputValue(val);
    if (val.length >= 3) {
      debouncedSearch(val);
      setDropdownOpen(true);
    } else {
      setOptions([]);
    }
  };

  const handleSelect = (opt: SuburbOption) => {
    setSelectedName(opt.suburbName);
    onChange?.(opt.suburbID, { ...opt, name: opt.suburbName });
    setDropdownOpen(false);
    setInputValue(opt.suburbName);
    const el: any = document.activeElement;
    if (el) el.blur();
  };

  const handleFocus = () => {
    if (options.length > 0) {
      setDropdownOpen(true);
    }
    const selectedOption = options.find((opt) => opt.suburbID === value);
    if (selectedOption) {
      setInputValue(selectedOption.suburbName);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const keyword = inputValueRef.current;
      const hasNoSearchResult =
        keyword.length >= 3 && !loadingRef.current && optionsRef.current.length === 0;

      setDropdownOpen(false);
      if (selectedNameRef.current && (!keyword || hasNoSearchResult)) {
        setInputValue(selectedNameRef.current);
      }
    }, 200);
  };

  return (
    <div
      className={`${styles["comp-suburb-select"]} ${className || ""}`}
      ref={wrapperRef}
    >
      <Input
        className={styles["search-input"]}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleSearch}
        onFocus={handleFocus}
        onBlur={handleBlur}
        clearable
        clearTrigger="always"
      />

      {dropdownOpen && (
        <div className={styles["dropdown"]}>
          {loading ? (
            <div className={styles["loading-box"]}>
              <Loading type="spinner" size={24} color="#e6415f" />
            </div>
          ) : options.length > 0 ? (
            options.map((opt) => (
              <div
                key={opt.suburbID}
                className={`${styles["dropdown-item"]} ${
                  opt.suburbID == value ? styles["dropdown-item-active"] : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                <h2 className={styles["item-title"]}>{opt.suburbName}</h2>
                <p className={styles["item-desc"]}>{opt.description}</p>
              </div>
            ))
          ) : inputValue.length >= 3 ? (
            <div className={styles["empty-box"]}>No suburbs found</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
